import React, { useRef, useState } from 'react';
import { cropRectFor, type CropPick } from '../../lib/stillNormalize';

type Props = {
  src: string;
  /** Output width / height. */
  ratio: number;
  value: CropPick;
  onChange: (next: CropPick) => void;
  label?: string;
};

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/** Largest window of `ratio` over the photo; the user only slides it to where the crop goes. */
export default function CropPicker({ src, ratio, value, onChange, label }: Props) {
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; pick: CropPick } | null>(null);

  const rect = size ? cropRectFor(size, ratio, value) : null;
  const freeX = size && rect ? size.width - rect.width : 0;
  const freeY = size && rect ? size.height - rect.height : 0;
  const horizontal = freeX >= freeY;

  /** Natural px per displayed px. */
  const scale = () => (size && stageRef.current?.clientWidth ? size.width / stageRef.current.clientWidth : 1);

  const placed = (start: CropPick, dx: number, dy: number): CropPick => ({
    ox: freeX > 0.5 ? clamp01(start.ox + dx / freeX) : 0.5,
    oy: freeY > 0.5 ? clamp01(start.oy + dy / freeY) : 0.5,
  });

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!size || !rect || !stageRef.current) return;
    e.preventDefault();
    let pick = value;
    if (!(e.target as HTMLElement).closest('.tb-crop-frame')) {
      const box = stageRef.current.getBoundingClientRect();
      const k = scale();
      pick = placed(
        { ox: 0, oy: 0 },
        (e.clientX - box.left) * k - rect.width / 2,
        (e.clientY - box.top) * k - rect.height / 2,
      );
      onChange(pick);
    }
    drag.current = { x: e.clientX, y: e.clientY, pick };
    stageRef.current.setPointerCapture(e.pointerId);
    setDragging(true);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    const k = scale();
    onChange(placed(d.pick, (e.clientX - d.x) * k, (e.clientY - d.y) * k));
  };

  const endDrag = () => {
    drag.current = null;
    setDragging(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const back = horizontal ? 'ArrowLeft' : 'ArrowUp';
    const fwd = horizontal ? 'ArrowRight' : 'ArrowDown';
    if (e.key !== back && e.key !== fwd) return;
    e.preventDefault();
    e.stopPropagation();
    const step = (e.shiftKey ? 0.1 : 0.02) * (e.key === fwd ? 1 : -1);
    onChange(horizontal ? { ...value, ox: clamp01(value.ox + step) } : { ...value, oy: clamp01(value.oy + step) });
  };

  const axis = horizontal ? 'ox' : 'oy';
  const presets: Array<[string, number]> = horizontal
    ? [['Esquerda', 0], ['Centro', 0.5], ['Direita', 1]]
    : [['Topo', 0], ['Centro', 0.5], ['Base', 1]];

  return (
    <div className="tb-crop">
      {label && <div className="tb-field-label">{label}</div>}
      <div
        ref={stageRef}
        className={`tb-crop-stage${dragging ? ' is-dragging' : ''}${horizontal ? '' : ' is-vertical'}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <img
          src={src}
          alt=""
          draggable={false}
          onLoad={(e) => setSize({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })}
        />
        {rect && size && (
          <div
            className="tb-crop-frame"
            role="group"
            tabIndex={0}
            aria-label="Área do recorte: arraste ou use as setas"
            onKeyDown={onKeyDown}
            style={{
              left: `${(rect.x / size.width) * 100}%`,
              top: `${(rect.y / size.height) * 100}%`,
              width: `${(rect.width / size.width) * 100}%`,
              height: `${(rect.height / size.height) * 100}%`,
            }}
          >
            <span className="tb-crop-grid" />
            <span className="tb-crop-corner is-tl" />
            <span className="tb-crop-corner is-tr" />
            <span className="tb-crop-corner is-bl" />
            <span className="tb-crop-corner is-br" />
          </div>
        )}
      </div>
      <div className="tb-crop-presets" role="radiogroup" aria-label="Posição do recorte">
        {presets.map(([name, v]) => {
          const on = Math.abs(value[axis] - v) < 0.01;
          return (
            <button
              key={name}
              type="button"
              role="radio"
              aria-checked={on}
              className={on ? 'is-on' : ''}
              onClick={() => onChange({ ...value, [axis]: v })}
            >
              {name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
